import { postApi } from "../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../headOfLineRole";

/** Basic full-address email format (local@domain.tld). */
export const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

export const EMAIL_FORMAT_ERROR =
  "Enter a valid email address (e.g. name@example.com)";

export function isValidEmailFormat(email) {
  const trimmed = (email || "").trim();
  if (!trimmed) return false;
  return EMAIL_FORMAT_REGEX.test(trimmed);
}

export function getEmailFormatError(email) {
  if (!email || !email.trim()) return "";
  return isValidEmailFormat(email) ? "" : EMAIL_FORMAT_ERROR;
}

/** Required check + format; empty is ok when not required. */
export function resolveEmailValidationError(email, { required = false } = {}) {
  const trimmed = (email || "").trim();
  if (!trimmed) {
    return required ? "Email is required" : "";
  }
  return getEmailFormatError(trimmed);
}

export const checkEmailExistsUtils = async (email, emailType = "primary",{
    familyTreeId,
    parentMember,
    setFieldValidationErrors,
    setSpouseValidationErrors,
    setAdditionalSpouseErrors,
  }) => {
    if (!email || !email.trim()) {
      console.log("⚠️ Email is empty, skipping validation");
      return;
    }

    // Prefer canonical tree id from loggedIn_user_role; then props
    let extractedFamilyTreeId = getFamilyTreeIdFromStorage();

    // Fallback to props if not found in localStorage
    if (!extractedFamilyTreeId) {
      extractedFamilyTreeId =
        familyTreeId ||
        parentMember?.family_tree_id ||
        parentMember?.familyTreeId ||
        null;
    }

    console.log("🔍 Checking email:", email);
    console.log("📋 Family Tree ID:", extractedFamilyTreeId);

    if (!extractedFamilyTreeId) {
      console.warn("⚠️ Family tree ID not available for email validation");
      return;
    }

    const formatError = getEmailFormatError(email);
    if (formatError) {
      if (emailType === "primary") {
        setFieldValidationErrors((prev) => ({ ...prev, email: formatError }));
      } else if (emailType === "spouse") {
        setSpouseValidationErrors((prev) => ({ ...prev, email: formatError }));
      } else if (emailType.startsWith("additional-")) {
        const spouseId = emailType.replace("additional-", "");
        setAdditionalSpouseErrors((prev) => ({
          ...prev,
          [spouseId]: { ...prev[spouseId], email: formatError },
        }));
      }
      return;
    }

    try {
      console.log("📡 Calling API: /check-email-in-family-tree", {
        family_tree_id: extractedFamilyTreeId,
        email: email.trim(),
      });
      const response = await postApi("/check-email-in-family-tree", {
        family_tree_id: extractedFamilyTreeId,
        email: email.trim(),
      });
      console.log("✅ API Response:", response);

      // Handle response
      if (response?.statusCode === 200) {
        // Email is available
        if (response?.data?.email_exists === false) {
          // Clear error if email is available
          if (emailType === "primary") {
            setFieldValidationErrors((prev) => ({
              ...prev,
              email: "",
            }));
          } else if (emailType === "spouse") {
            setSpouseValidationErrors((prev) => ({
              ...prev,
              email: "",
            }));
          } else if (emailType.startsWith("additional-")) {
            const spouseId = emailType.replace("additional-", "");
            setAdditionalSpouseErrors((prev) => ({
              ...prev,
              [spouseId]: {
                ...prev[spouseId],
                email: "",
              },
            }));
          }
        }
      } else if (response?.statusCode === 400) {
        // Email already exists
        const errorMessage = response?.message || "Email already exists in this family tree";
        if (emailType === "primary") {
          setFieldValidationErrors((prev) => ({
            ...prev,
            email: errorMessage,
          }));
        } else if (emailType === "spouse") {
          setSpouseValidationErrors((prev) => ({
            ...prev,
            email: errorMessage,
          }));
        } else if (emailType.startsWith("additional-")) {
          const spouseId = emailType.replace("additional-", "");
          setAdditionalSpouseErrors((prev) => ({
            ...prev,
            [spouseId]: {
              ...prev[spouseId],
              email: errorMessage,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Error checking email:", error);
      // On error, check if it's a 400 status (email exists)
      // Handle different error response structures
      const errorResponse = error?.response?.data || error?.data || error;
      if (errorResponse?.statusCode === 400) {
        const errorMessage = errorResponse?.message || "Email already exists in this family tree";
        if (emailType === "primary") {
          setFieldValidationErrors((prev) => ({
            ...prev,
            email: errorMessage,
          }));
        } else if (emailType === "spouse") {
          setSpouseValidationErrors((prev) => ({
            ...prev,
            email: errorMessage,
          }));
        } else if (emailType.startsWith("additional-")) {
          const spouseId = emailType.replace("additional-", "");
          setAdditionalSpouseErrors((prev) => ({
            ...prev,
            [spouseId]: {
              ...prev[spouseId],
              email: errorMessage,
            },
          }));
        }
      }
    }
  };